import { useState, useEffect } from 'react';
import { useAuth } from '../../../AuthContext';
import { fetchData, postData, updateData, deleteData, activateData } from '../../../apiService';
import LogoLoader from '../../common/LogoLoader';
import { useSessionValidation } from '../../../utils/sessionValidation';
import useAuditLog, { AUDIT_ACTION } from '../../../hooks/useAuditLog';
import { Add, Refresh, Warning, CheckCircle, Search, AccountBalance } from '@mui/icons-material';
import {
  Button, Tooltip, Chip, Alert, Snackbar,
  Dialog, DialogTitle, DialogContent, DialogActions, Typography, Box,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper,
  TextField, InputAdornment, IconButton,
  useMediaQuery
} from '@mui/material';
import JournalEntryModal from './JournalEntryModal';

const JournalEntryIndex = () => {
  useSessionValidation();
  const { companyId, branchId, token, userId, permissions } = useAuth();
  const { logAudit } = useAuditLog();

  const hasCreatePermission = permissions?.includes('Create Master');
  const hasEditPermission = permissions?.includes('Edit Master');
  const hasDeletePermission = permissions?.includes('Delete Master');

  const [openModal, setOpenModal] = useState(false);
  const [modalAction, setModalAction] = useState('');
  const [journalEntries, setJournalEntries] = useState([]);
  const [masters, setMasters] = useState([]);
  const [gstRates, setGstRates] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const isMobile = useMediaQuery('(max-width:768px)');
  const [showSearch, setShowSearch] = useState(false);

  const fmt = (date) => {
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  };

  const [fromDate, setFromDate] = useState(() => { const t = new Date(); return fmt(new Date(t.getFullYear(), t.getMonth(), 1)); });
  const [toDate, setToDate] = useState(() => fmt(new Date()));
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [formData, setFormData] = useState({});
  const [journalEntryDetails, setJournalEntryDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [deleteItemId, setDeleteItemId] = useState(null);
  const [deleteItemNo, setDeleteItemNo] = useState('');
  const [activateModalOpen, setActivateModalOpen] = useState(false);
  const [activateItemId, setActivateItemId] = useState(null);
  const [activateItemNo, setActivateItemNo] = useState('');


  useEffect(() => {
    if (companyId && token && fromDate && toDate) fetchAllData();
  }, [companyId, token, fromDate, toDate]);

  const fetchJournalEntries = async () => {
    try {
      const activeRes = await fetchData('JournalEntryApi', `/ActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
      const activeData = (activeRes.response_data || activeRes || []).map(i => ({ ...i, IsActive: i.IsActive ?? true }));
      let inactiveData = [];
      try {
        const inactiveRes = await fetchData('JournalEntryApi', `/InActiveList/${companyId}/${branchId}/${fromDate}/${toDate}`, token);
        inactiveData = (inactiveRes.response_data || inactiveRes || []).map(i => ({ ...i, IsActive: false }));
      } catch (e) { console.error(e); }
      setJournalEntries([...activeData, ...inactiveData]);
    } catch (e) { console.error(e); setJournalEntries([]); }
  };

  const fetchMasters = async () => {
    try {
      const res = await fetchData('JournalEntryApi', `/Master/${companyId}`, token);
      setMasters(res.response_data || res || []);
    } catch (e) { setMasters([]); }
  };

  const fetchGstRates = async () => {
    try {
      const res = await fetchData('JournalEntryApi', `/GetGSTList/${companyId}`, token);
      setGstRates(res.response_data || res || []);
    } catch (e) { setGstRates([]); }
  };

  const fetchMaxBillNo = async () => {
    try {
      const res = await fetchData('JournalEntryApi', `/MaxBillno/${branchId || 1}/${companyId}`, token);
      const d = res.response_data || res || {};
      return { billNumber: d.BillNumber || d.billNumber || 1, billPrefix: d.BillPrefix || d.billPrefix || '', finYear: d.FinYear || d.finYear || '' };
    } catch (e) { return { billNumber: 1, billPrefix: '', finYear: '' }; }
  };

  const fetchAllData = async () => {
    setLoading(true);
    try { await Promise.allSettled([fetchJournalEntries(), fetchMasters(), fetchGstRates()]); }
    catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const handleOpenModal = async (action, item = null) => {
    setModalAction(action);
    if (action === 'view') {
      const recordId = item?.Id || 0;
      logAudit(AUDIT_ACTION.VIEW, 'JournalEntryApi', recordId);
    }
    if (action === 'create') {
      const maxBillData = await fetchMaxBillNo();
      const billNumber = maxBillData.billPrefix && maxBillData.finYear
        ? `${maxBillData.billPrefix}/${maxBillData.billNumber}/${maxBillData.finYear}`
        : maxBillData.billNumber.toString();
      setFormData({ Id: 0, CompanyId: companyId, BranchId: branchId, BillNumber: billNumber, BillDate: new Date().toISOString().split('T')[0], IsType: 'Credit', MasterId: '', Amount: 0, TotalChildAmount: 0, Remarks: '', IsActive: true });
      setJournalEntryDetails([]);
    } else if (action === 'edit' || action === 'view') {
      setModalLoading(true);
      try {
        const res = await fetchData('JournalEntryApi', `/JournalEntryData/${item.Id}`, token);
        const jd = res.response_data || res;
        const jEntry = jd.JournalEntry;
        setFormData({ ...jEntry, BillDate: jEntry.BillDate ? jEntry.BillDate.split('T')[0] : new Date().toISOString().split('T')[0], IsType: jEntry.IsType === true ? 'Credit' : 'Debit', MasterId: jEntry.MasterId || '' });
        const originalDetails = jd.JournalEntryDetails || [];
        const filtered = originalDetails.filter(d => d.MasterId !== jEntry.MasterId);
        setJournalEntryDetails(filtered);
        if (originalDetails.length > filtered.length) setError(`${originalDetails.length - filtered.length} child entries with the same head as Main Head have been removed.`);
      } catch (e) {
        console.error(e); setError('Failed to fetch journal entry details.');
        setFormData({ ...item, BillDate: item.BillDate ? item.BillDate.split('T')[0] : new Date().toISOString().split('T')[0], IsType: item.IsType === true ? 'Credit' : 'Debit', MasterId: item.MasterId || '' });
        setJournalEntryDetails((item.JournalEntryDetails || []).filter(d => d.MasterId !== item.MasterId));
      }
      setModalLoading(false);
    }
    setOpenModal(true);
  };

  const handleCloseModal = () => { setOpenModal(false); setFormData({}); setJournalEntryDetails([]); };

  const handleSave = async () => {
    setModalLoading(true);
    try {
      const dataToSend = {
        JournalEntry: {
          Id: modalAction === 'edit' ? formData.Id : 0, CompanyId: companyId, BranchId: branchId,
          BillDate: formData.BillDate, BillNumber: parseInt(formData.BillNumber) || 0,
          FinYear: formData.FinYear || new Date().getFullYear().toString(),
          IsType: formData.IsType === 'Credit', MasterId: parseInt(formData.MasterId) || 0,
          Amount: parseFloat(formData.Amount) || 0, Remarks: formData.Remarks || null, IsActive: true,
          CreatedBy: modalAction === 'create' ? userId : formData.CreatedBy,
          UpdatedBy: modalAction === 'edit' ? userId : undefined,
          CreatedDate: modalAction === 'create' ? new Date().toISOString() : formData.CreatedDate,
          UpdatedDate: modalAction === 'edit' ? new Date().toISOString() : undefined
        }
      };
      if (journalEntryDetails.length > 0) {
        dataToSend.JournalEntryDetails = journalEntryDetails.map(d => ({
          Id: d.Id || 0, CompanyId: companyId, BranchId: branchId,
          JournalEntryId: modalAction === 'edit' ? formData.Id : 0,
          MasterId: parseInt(d.MasterId), Amount: parseFloat(d.Amount) || 0,
          Remarks: d.Remarks || null, IsActive: true,
          CreatedBy: modalAction === 'create' ? userId : d.CreatedBy,
          UpdatedBy: modalAction === 'edit' ? userId : undefined,
          CreatedDate: modalAction === 'create' ? new Date().toISOString() : d.CreatedDate,
          UpdatedDate: modalAction === 'edit' ? new Date().toISOString() : undefined
        }));
      }
      if (modalAction === 'create') { await postData('JournalEntryApi', '', dataToSend, token); setSuccess('Journal Entry created successfully!');
        logAudit(AUDIT_ACTION.CREATE, 'JournalEntryApi', 0, null, { BillNumber: formData.BillNumber, BillDate: formData.BillDate, Amount: formData.Amount });
      }
      else { await updateData('JournalEntryApi', formData.Id, dataToSend, token); setSuccess('Journal Entry updated successfully!');
        logAudit(AUDIT_ACTION.UPDATE, 'JournalEntryApi', formData.Id, { ...selectedItem }, { ...formData });
      }
      handleCloseModal();
      await fetchJournalEntries();
    } catch (e) { console.error(e); setError('Failed to save journal entry.'); }
    setModalLoading(false);
  };

  const handleDelete = async () => {
    try { await deleteData('JournalEntryApi', deleteItemId, token); setSuccess('Journal Entry deleted successfully!');
      logAudit(AUDIT_ACTION.DELETE, 'JournalEntryApi', deleteItemId, { BillNumber: deleteItemNo }, null);
      setDeleteModalOpen(false); await fetchJournalEntries(); }
    catch (e) { setError('Failed to delete journal entry.'); }
  };

  const handleActivate = async () => {
    try { await activateData('JournalEntryApi', activateItemId, token); setSuccess('Journal Entry activated successfully!'); setActivateModalOpen(false); await fetchJournalEntries(); }
    catch (e) { setError('Failed to activate journal entry.'); }
  };

  const filteredData = journalEntries.filter(item => {
    const matchesSearch = !searchTerm ||
      item.BillNumber?.toString().toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.Remarks?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || (filterStatus === 'active' && item.IsActive) || (filterStatus === 'inactive' && !item.IsActive);
    return matchesSearch && matchesStatus;
  });

  const totalPages = Math.ceil(filteredData.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedData = filteredData.slice(startIndex, startIndex + rowsPerPage);

  const formatCurrency = (amount) => `\u20B9${(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const formatDate = (ds) => ds ? new Date(ds).toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';


  return (
    <div style={{ minWidth: '100%', background: '#f5f7fa', padding: '10px', position: 'relative' }}>
      <LogoLoader open={loading || modalLoading} message="Loading Journal Entries..." />

      <div style={{ maxWidth: '1400px', margin: '0 auto', background: 'white', borderRadius: '12px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', padding: '12px', position: 'relative', color: 'black', fontFamily: 'Times New Roman' }}>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', paddingBottom: '8px', borderBottom: '1px solid #f0f0f0' }}>
          <h4 style={{ fontSize: '17px', fontFamily: 'Times New Roman', fontWeight: '700', margin: 0, textTransform: 'uppercase', color: 'black' }}>Journal Entry</h4>
        </div>

        {isMobile ? (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center', overflowX: 'auto', pb: 0.5, pt: 1, WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', '&::-webkit-scrollbar': { display: 'none' }, overflowY: 'visible' }}>
              <Tooltip title="Search" arrow>
                <IconButton onClick={() => setShowSearch(v => !v)} size="small" sx={{ border: `1px solid ${showSearch ? '#667eea' : '#d1d5db'}`, borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, bgcolor: showSearch ? '#eff6ff' : 'transparent' }}>
                  <Search sx={{ fontSize: '16px', color: showSearch ? '#667eea' : 'inherit' }} />
                </IconButton>
              </Tooltip>
              <input type="date" lang="en-GB" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <input type="date" lang="en-GB" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0, width: 120 }} />
              <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={{ height: '32px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 6px', fontSize: '11px', backgroundColor: '#f8f9fa', flexShrink: 0 }}>
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
              <Tooltip title="Refresh" arrow>
                <IconButton onClick={fetchAllData} disabled={loading} size="small" sx={{ border: '1px solid #d1d5db', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0 }}>
                  <Refresh sx={{ fontSize: '16px' }} />
                </IconButton>
              </Tooltip>
              {hasCreatePermission && (
                <Tooltip title="Add Journal Entry" arrow>
                  <IconButton onClick={() => handleOpenModal('create')} size="small" sx={{ background: '#2C3E6E', color: 'white', borderRadius: '8px', height: '32px', width: '32px', flexShrink: 0, '&:hover': { background: '#1a2d5a' } }}>
                    <Add sx={{ fontSize: '18px' }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>
            {showSearch && (
              <Box sx={{ mt: 0.75 }}>
                <TextField autoFocus fullWidth size="small" placeholder="Search by Entry No, Remarks..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                  InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '18px' }} /></InputAdornment> }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '32px' } }}
                />
              </Box>
            )}
          </Box>
        ) : (
          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', marginBottom: '12px', flexWrap: 'wrap' }}>
            <TextField size="small" placeholder="Search by Entry No, Remarks..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search style={{ color: '#667eea', fontSize: '20px' }} /></InputAdornment> }}
              sx={{ flex: 1, minWidth: 180, '& .MuiOutlinedInput-root': { borderRadius: '8px', backgroundColor: '#f8f9fa', fontSize: '13px', height: '29px' } }}
            />
            <input type="date" lang="en-GB" value={fromDate} onChange={(e) => { setFromDate(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <input type="date" lang="en-GB" value={toDate} onChange={(e) => { setToDate(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }} />
            <select value={filterStatus} onChange={(e) => { setFilterStatus(e.target.value); setCurrentPage(1); }} style={{ height: '29px', border: '1px solid #d1d5db', borderRadius: '8px', padding: '0 8px', fontSize: '12px', backgroundColor: '#f8f9fa' }}>
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
            <Tooltip title="Refresh" arrow>
              <button onClick={fetchAllData} disabled={loading} style={{ height: '29px', width: '29px', border: '1px solid #d1d5db', borderRadius: '8px', background: '#f8f9fa', cursor: loading ? 'not-allowed' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Refresh sx={{ fontSize: '16px', color: '#6b7280' }} />
              </button>
            </Tooltip>
            {hasCreatePermission && (
              <Tooltip title="Add Journal Entry" arrow>
                <Button variant="contained" onClick={() => handleOpenModal('create')}
                  sx={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #1a2a4a 100%)', color: 'white', padding: '4px 12px', borderRadius: '6px', fontSize: '12px', fontWeight: '700', minHeight: '29px', textTransform: 'none', whiteSpace: 'nowrap' }}>
                  <Add sx={{ fontSize: '16px' }} /><span style={{ marginLeft: '4px' }}>Add Journal Entry</span>
                </Button>
              </Tooltip>
            )}
          </div>
        )}

        <div style={{ marginBottom: '10px' }}>
          <TableContainer component={Paper} style={{ boxShadow: '0 2px 8px rgba(0,0,0,0.06)', borderRadius: '10px', overflow: 'hidden' }}>
            <Table sx={{ minWidth: 650 }}>
              <TableHead>
                <TableRow style={{ background: 'linear-gradient(135deg, #2C3E6E 0%, #1a2a4a 100%)', height: '27px' }}>
                  {['Sl No.', 'Journal Entry No', 'Date', 'Type', 'Main Head', 'Amount', 'Remarks', 'Status', 'Actions'].map((h, i) => (
                    <TableCell key={i} style={{ color: 'white', fontWeight: '600', fontSize: '11px', textTransform: 'uppercase', letterSpacing: '0.5px', padding: '4px 12px', height: '27px', textAlign: i === 5 ? 'right' : i === 8 ? 'right' : 'left' }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? paginatedData.map((item, index) => (
                  <TableRow key={item.Id} style={{ background: index % 2 === 0 ? '#ffffff' : '#f8f9fa', borderBottom: '2px solid #f0f0f0' }}>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', textAlign: 'center' }}>{startIndex + index + 1}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 600 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                        <AccountBalance sx={{ fontSize: 16, color: item.IsType === true ? '#429178' : '#dc6363' }} />
                        {item.BillNumber}{item.FinYear ? `/${item.FinYear}` : ''}
                      </div>
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{formatDate(item.BillDate)}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                      <Chip label={item.IsType === true ? 'Credit' : item.IsType === false ? 'Debit' : '-'} size="small"
                        sx={{ backgroundColor: item.IsType === true ? '#d1fae5' : '#fef3c7', color: item.IsType === true ? '#065f46' : '#92400e', fontSize: '0.65rem', height: '20px', fontWeight: 500 }} />
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748' }}>{masters.find(m => m.Id === item.MasterId)?.Name || '-'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', fontWeight: 700, textAlign: 'right' }}>{formatCurrency(item.Amount)}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', color: '#2d3748', maxWidth: 160, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.Remarks || '-'}</TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px' }}>
                      <Chip label={item.IsActive ? 'Active' : 'Inactive'} color={item.IsActive ? 'success' : 'default'} size="small" sx={{ fontSize: '0.65rem', height: '20px' }} />
                    </TableCell>
                    <TableCell style={{ fontSize: '12px', padding: '8px 12px', textAlign: 'right' }}>
                      <Box sx={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                        <Tooltip title="View" arrow><span style={{ cursor: 'pointer', fontSize: '14px' }} onClick={() => handleOpenModal('view', item)}>👁️</span></Tooltip>
                        {hasEditPermission && item.IsActive && (
                          <Tooltip title="Edit" arrow><span style={{ cursor: 'pointer', fontSize: '14px' }} onClick={() => handleOpenModal('edit', item)}>✏️</span></Tooltip>
                        )}
                        {hasDeletePermission && item.IsActive && (
                          <Tooltip title="Delete" arrow><span style={{ cursor: 'pointer', fontSize: '14px' }} onClick={() => { setDeleteItemId(item.Id); setDeleteItemNo(item.BillNumber); setDeleteModalOpen(true); }}>🗑️</span></Tooltip>
                        )}
                        {hasDeletePermission && !item.IsActive && (
                          <Tooltip title="Activate" arrow><span style={{ cursor: 'pointer', fontSize: '14px' }} onClick={() => { setActivateItemId(item.Id); setActivateItemNo(item.BillNumber); setActivateModalOpen(true); }}>✅</span></Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  </TableRow>
                )) : (
                  <TableRow>
                    <TableCell colSpan={9} style={{ textAlign: 'center', padding: '20px', color: '#6b7280', fontSize: '13px' }}>No journal entries found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '10px', flexWrap: 'wrap', gap: '8px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '12px', color: '#6b7280' }}>
            <span>Rows per page:</span>
            <select value={rowsPerPage} onChange={(e) => { setRowsPerPage(parseInt(e.target.value)); setCurrentPage(1); }}
              style={{ height: '26px', border: '1px solid #d1d5db', borderRadius: '6px', padding: '0 6px', fontSize: '12px', backgroundColor: '#f8f9fa' }}>
              {[10, 25, 50, 100].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <span>{startIndex + 1}–{Math.min(startIndex + rowsPerPage, filteredData.length)} of {filteredData.length}</span>
          </div>
          <div style={{ display: 'flex', gap: '4px' }}>
            {['First', 'Prev'].map((label, i) => (
              <button key={label} onClick={() => setCurrentPage(i === 0 ? 1 : currentPage - 1)} disabled={currentPage === 1}
                style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px', background: 'white', cursor: currentPage === 1 ? 'not-allowed' : 'pointer', opacity: currentPage === 1 ? 0.5 : 1 }}>{label}</button>
            ))}
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              const page = Math.max(1, Math.min(totalPages - 4, currentPage - 2)) + i;
              return <button key={page} onClick={() => setCurrentPage(page)}
                style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px', cursor: 'pointer', background: currentPage === page ? '#2C3E6E' : 'white', color: currentPage === page ? 'white' : '#374151' }}>{page}</button>;
            })}
            {['Next', 'Last'].map((label, i) => (
              <button key={label} onClick={() => setCurrentPage(i === 0 ? currentPage + 1 : totalPages)} disabled={currentPage === totalPages || totalPages === 0}
                style={{ padding: '4px 8px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '11px', background: 'white', cursor: (currentPage === totalPages || totalPages === 0) ? 'not-allowed' : 'pointer', opacity: (currentPage === totalPages || totalPages === 0) ? 0.5 : 1 }}>{label}</button>
            ))}
          </div>
        </div>
      </div>

      <JournalEntryModal open={openModal} onClose={handleCloseModal} modalAction={modalAction}
        formData={formData} setFormData={setFormData}
        journalEntryDetails={journalEntryDetails} setJournalEntryDetails={setJournalEntryDetails}
        onSave={handleSave} masters={masters} gstRates={gstRates} loading={modalLoading}
        companyId={companyId} token={token} fetchMaxBillNo={fetchMaxBillNo}
        branchId={branchId} fromDate={fromDate} toDate={toDate} />

      <Dialog open={deleteModalOpen} onClose={() => setDeleteModalOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', p: 1 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ bgcolor: '#fee2e2', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Warning sx={{ color: '#dc2626', fontSize: 22 }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Confirm Delete</Typography>
          </Box>
        </DialogTitle>
        <DialogContent><Typography sx={{ fontSize: '0.9rem', color: '#374151' }}>Are you sure you want to delete Journal Entry "{deleteItemNo}"?</Typography></DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button onClick={() => setDeleteModalOpen(false)} variant="outlined" size="small" sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleDelete} variant="contained" size="small" sx={{ bgcolor: '#dc2626', '&:hover': { bgcolor: '#b91c1c' }, textTransform: 'none' }}>Delete</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={activateModalOpen} onClose={() => setActivateModalOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: '12px', p: 1 } }}>
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box sx={{ bgcolor: '#d1fae5', borderRadius: '50%', width: 40, height: 40, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CheckCircle sx={{ color: '#059669', fontSize: 22 }} />
            </Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Confirm Activate</Typography>
          </Box>
        </DialogTitle>
        <DialogContent><Typography sx={{ fontSize: '0.9rem', color: '#374151' }}>Are you sure you want to activate Journal Entry "{activateItemNo}"?</Typography></DialogContent>
        <DialogActions sx={{ px: 2, pb: 2, gap: 1 }}>
          <Button onClick={() => setActivateModalOpen(false)} variant="outlined" size="small" sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleActivate} variant="contained" size="small" sx={{ bgcolor: '#059669', '&:hover': { bgcolor: '#047857' }, textTransform: 'none' }}>Activate</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!success} autoHideDuration={6000} onClose={() => setSuccess('')}>
        <Alert onClose={() => setSuccess('')} severity="success">{success}</Alert>
      </Snackbar>
      <Snackbar open={!!error} autoHideDuration={6000} onClose={() => setError('')}>
        <Alert onClose={() => setError('')} severity="error">{error}</Alert>
      </Snackbar>
    </div>
  );
};

export default JournalEntryIndex;

